import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  DocumentTextIcon,
  PlusIcon,
  XMarkIcon,
  ShieldCheckIcon,
  SparklesIcon,
  Bars2Icon,
} from '@heroicons/react/24/outline';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { DOCUMENT_CATEGORIES } from '../../utils/documentTypes';
import { useAuth } from '../../contexts/AuthContext';

const GOVT_DOC_LABEL =
  'Government ID documents (such as Aadhaar, PAN, Passport, Driving Licence) are not permitted to be requested or collected under safety policies.';

function isGovernmentIdDoc({ type, subtype, label }) {
  return false;
}

export default function DocumentCollectionConfig({
  requestedDocuments = [],
  onChange,
  enableVerification = false,
  onVerificationChange,
}) {
  const { user, isPlatformSuperAdmin } = useAuth();
  const isVerificationFeatureEnabled = isPlatformSuperAdmin || user?.features?.document_verification === true;
  const [customInput, setCustomInput] = useState('');

  const selectedKeys = useMemo(() => {
    const set = new Set();
    requestedDocuments.forEach((doc) => {
      if (doc.type !== 'other') {
        set.add(`${doc.type}::${doc.subtype}`);
      }
    });
    return set;
  }, [requestedDocuments]);

  const hasGovtIdSelected = useMemo(() => {
    return requestedDocuments.some((doc) => doc.type === 'govt_id');
  }, [requestedDocuments]);

  const handleToggleDoc = (catType, docSubtype, label) => {
    const key = `${catType}::${docSubtype}`;
    if (selectedKeys.has(key)) {
      // Remove it
      const nextDocs = requestedDocuments.filter(
        (doc) => !(doc.type === catType && doc.subtype === docSubtype)
      );
      onChange(nextDocs);
    } else {
      // Add it
      const newDoc = { type: catType, subtype: docSubtype, label, required: true };
      if (isGovernmentIdDoc(newDoc)) {
        toast.error(GOVT_DOC_LABEL);
        return;
      }
      onChange([...requestedDocuments, newDoc]);
    }
  };

  const handleAddCustom = (e) => {
    e.preventDefault();
    const label = customInput.trim();
    if (!label) return;

    const newDoc = { type: 'other', subtype: 'custom', label, required: true };
    if (isGovernmentIdDoc(newDoc)) {
      toast.error(GOVT_DOC_LABEL);
      return;
    }

    // Check if custom document with same label already exists
    const exists = requestedDocuments.some(
      (doc) => doc.label.toLowerCase() === label.toLowerCase()
    );
    if (exists) {
      toast.error('A document with this label is already selected.');
      return;
    }

    onChange([...requestedDocuments, newDoc]);
    setCustomInput('');
  };

  const handleRemoveDoc = (indexToRemove) => {
    const nextDocs = requestedDocuments.filter((_, idx) => idx !== indexToRemove);
    onChange(nextDocs);
  };

  const handleToggleRequired = (indexToToggle) => {
    const nextDocs = requestedDocuments.map((doc, idx) => {
      if (idx === indexToToggle) {
        return { ...doc, required: doc.required === false ? true : false };
      }
      return doc;
    });
    onChange(nextDocs);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const startIndex = result.source.index;
    const endIndex = result.destination.index;
    
    const reorderedDocs = Array.from(requestedDocuments);
    const [removed] = reorderedDocs.splice(startIndex, 1);
    reorderedDocs.splice(endIndex, 0, removed);
    
    onChange(reorderedDocs);
  };

  return (
    <div className="space-y-6 w-full animate-fadeIn">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
        {/* Left: Document checklist selectors */}
        <div className="md:col-span-7 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">
              Select Documents by Category
            </h4>
            <span className="text-[11px] font-medium text-slate-500">
              Click items to toggle selection
            </span>
          </div>

          <div className="grid grid-cols-1 gap-3.5">
            {DOCUMENT_CATEGORIES.filter((cat) => cat.type !== 'other').map((cat) => (
              <div
                key={cat.type}
                className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-[#007BFF] bg-[#007BFF]/10 px-2.5 py-1 rounded-lg">
                    {cat.label}
                  </span>
                </div>
                <div className="space-y-2">
                  {cat.documents.map((doc) => {
                    const key = `${cat.type}::${doc.subtype}`;
                    const isSelected = selectedKeys.has(key);
                    const requestedDoc = requestedDocuments.find(
                      (d) => d.type === cat.type && d.subtype === doc.subtype
                    );
                    const isRequired = requestedDoc ? requestedDoc.required !== false : true;

                    return (
                      <div
                        key={doc.subtype}
                        className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl border text-sm transition-all duration-200 ${
                          isSelected
                            ? 'border-[#007BFF] bg-[#007BFF]/[0.03] shadow-sm'
                            : 'border-slate-200/80 hover:border-slate-300 hover:bg-slate-50/60'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => handleToggleDoc(cat.type, doc.subtype, doc.label)}
                          className="flex items-center gap-3 text-left flex-1 min-w-0"
                        >
                          <div
                            className={`w-4 h-4 rounded-md border flex items-center justify-center shrink-0 transition-colors ${
                              isSelected
                                ? 'border-[#007BFF] bg-[#007BFF] text-white'
                                : 'border-slate-300 bg-white'
                            }`}
                          >
                            {isSelected && (
                              <svg
                                className="w-2.5 h-2.5 stroke-[3]"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            )}
                          </div>
                          <span
                            className={`text-xs sm:text-sm truncate ${
                              isSelected ? 'text-slate-900 font-semibold' : 'text-slate-700'
                            }`}
                          >
                            {doc.label}
                          </span>
                        </button>

                        {isSelected && (
                          <div className="flex items-center border-l border-slate-200 pl-3 ml-2 shrink-0">
                            <label className="flex items-center gap-1.5 cursor-pointer text-xs font-semibold text-slate-700 select-none">
                              <input
                                type="checkbox"
                                checked={isRequired}
                                onChange={() => {
                                  const idx = requestedDocuments.findIndex(
                                    (d) => d.type === cat.type && d.subtype === doc.subtype
                                  );
                                  if (idx !== -1) handleToggleRequired(idx);
                                }}
                                className="w-3.5 h-3.5 text-[#007BFF] rounded border-slate-300 focus:ring-[#007BFF] cursor-pointer"
                              />
                              Required
                            </label>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Add custom document form */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-4 sm:p-5 shadow-sm">
            <h5 className="text-sm font-semibold text-slate-900 mb-1">Request Custom Document</h5>
            <p className="text-xs text-slate-500 mb-3">
              Need another specific file? Add a custom document title below.
            </p>
            <form onSubmit={handleAddCustom} className="flex gap-2">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="e.g. Relieving Certificate, Previous Payslips"
                className="flex-1 px-3.5 py-2 border border-slate-300 rounded-xl text-xs sm:text-sm focus:outline-none focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/20"
              />
              <button
                type="submit"
                disabled={!customInput.trim()}
                className="px-4 py-2 rounded-xl bg-[#007BFF] hover:bg-[#0062CC] text-xs sm:text-sm font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed shrink-0 flex items-center gap-1.5 shadow-sm cursor-pointer"
              >
                <PlusIcon className="h-4 w-4" />
                Add
              </button>
            </form>
          </div>
        </div>

        {/* Right: Selected documents panel & Verification toggle */}
        <div className="md:col-span-5 space-y-4 md:sticky md:top-0">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">
              Selected Documents ({requestedDocuments.length})
            </h4>
          </div>

          <div className="bg-slate-50 border border-slate-200/90 rounded-2xl p-4 min-h-[220px] flex flex-col shadow-inner">
            {requestedDocuments.length === 0 ? (
              <div className="my-auto text-center py-10">
                <DocumentTextIcon className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                <p className="text-xs sm:text-sm text-slate-500 font-medium">
                  No documents selected
                </p>
                <p className="text-[11px] text-slate-400 mt-1 max-w-[180px] mx-auto">
                  Pick document items from the categories on the left.
                </p>
              </div>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="selected-documents-list">
                  {(provided) => (
                    <div
                      className="space-y-2 flex-1 overflow-y-auto max-h-[320px] pr-1"
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {requestedDocuments.map((doc, idx) => {
                        const dragKey = doc.type === 'other' ? `other-${doc.label}` : `${doc.type}-${doc.subtype}`;
                        return (
                          <Draggable key={dragKey} draggableId={dragKey} index={idx}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`flex items-center justify-between gap-2.5 bg-white p-3 rounded-xl border transition-all ${
                                  snapshot.isDragging
                                    ? 'border-[#007BFF] shadow-md ring-1 ring-[#007BFF]/20 z-50'
                                    : 'border-slate-200/90 shadow-sm hover:border-slate-300'
                                }`}
                              >
                                <div className="min-w-0 flex items-center gap-2.5 flex-1">
                                  <div
                                    {...provided.dragHandleProps}
                                    className="p-1 cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 rounded"
                                    title="Drag to reorder"
                                  >
                                    <Bars2Icon className="h-4 w-4" />
                                  </div>
                                  <div className="p-1.5 rounded-lg bg-[#007BFF]/10 text-[#007BFF] shrink-0">
                                    <DocumentTextIcon className="h-4 w-4" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-xs font-semibold text-slate-800 truncate leading-snug">
                                      {doc.label}
                                    </p>
                                    <div className="flex items-center gap-1.5 mt-0.5">
                                      <span className="text-[10px] font-medium text-slate-500 capitalize bg-slate-100 px-1.5 py-0.5 rounded">
                                        {doc.type === 'other' ? 'Custom' : doc.type}
                                      </span>
                                      {doc.required !== false && (
                                        <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                                          Required
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveDoc(idx)}
                                  className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors shrink-0"
                                  title="Remove document"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>

          {/* AI Document Verification Toggle — only visible when custom feature is enabled */}
          {isVerificationFeatureEnabled && (
            <>
              <div
                className={`flex items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5 transition-all duration-200 ${
                  enableVerification
                    ? 'bg-blue-50 border-blue-200'
                    : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <div
                    className={`p-1.5 rounded-lg shrink-0 transition-colors ${
                      enableVerification ? 'bg-[#007BFF] text-white' : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    <ShieldCheckIcon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold text-slate-900">Document Verification</span>
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">
                        <SparklesIcon className="h-2.5 w-2.5" />
                        AI OCR
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 leading-tight mt-0.5 truncate">
                      Auto-verify govt IDs and Bank Accounts
                    </p>
                  </div>
                </div>

                {/* Toggle switch */}
                <button
                  type="button"
                  role="switch"
                  aria-checked={enableVerification}
                  onClick={() => onVerificationChange && onVerificationChange(!enableVerification)}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#007BFF] focus:ring-offset-2 ${
                    enableVerification ? 'bg-[#007BFF]' : 'bg-slate-200'
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      enableVerification ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>


            </>
          )}
        </div>
      </div>
    </div>
  );
}

